import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, User } from "lucide-react";
import { api } from "../../api";
import { memberProfileTestIds } from "../../constants/testIds";

const GENDER_LABELS = { male: "Male", female: "Female", other: "Other" };

function formatDate(str) {
  if (!str) return null;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function calcAge(dobStr) {
  const d = new Date(dobStr || "");
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

function titleCase(str) {
  return (str || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// A single label/value pair — renders nothing when the value is empty, so the panel never shows
// a wall of "—" for fields a walk-in/lead never filled in.
function Field({ label, value, testId }) {
  if (!value) return null;
  return (
    <div data-testid={testId}>
      <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{label}</p>
      <p className="text-sm font-medium mt-0.5" style={{ color: "var(--scarlet-text)" }}>{value}</p>
    </div>
  );
}

function SectionLabel({ children }) {
  return <p className="eyebrow mb-2.5">{children}</p>;
}

// UX request: "whatever we have collected from the member till that day — onboarding info, KYC,
// DOB etc." — one shared, always-populated 360° panel mounted on BOTH the Owner Cockpit's
// MemberDetail.jsx and the Desk shell's MemberDetail.jsx (identical backend payload from
// `GET /members/{id}`, zero extra endpoints needed — every field below was already being
// captured + returned, just never surfaced on screen). `idPrefix` keeps testids scoped to each
// host page's existing namespace (owner-member-* / desk-member-*).
export function MemberProfileSection({ member, idPrefix }) {
  const ids = memberProfileTestIds(idPrefix);
  const [open, setOpen] = useState(true);
  const [photoUrl, setPhotoUrl] = useState(null);
  const profile = member.profile || {};
  const kyc = profile.kyc || {};
  const emergency = profile.emergency_contact;
  const flags = profile.medical_flags || {};
  const notes = member.notes || [];
  const age = calcAge(profile.dob);

  useEffect(() => {
    if (!profile.photo_key) { setPhotoUrl(null); return; }
    let objectUrl;
    api.get("/members/photo", { params: { photo_key: profile.photo_key }, responseType: "blob" })
      .then((res) => { objectUrl = URL.createObjectURL(res.data); setPhotoUrl(objectUrl); })
      .catch(() => setPhotoUrl(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [profile.photo_key]);

  const hasKyc = kyc.aadhaar_number || kyc.pan_number || kyc.other_document_number;
  const hasHealth = flags.fitness_goal || flags.fitness_level || flags.medical_history
    || (flags.medical_conditions || []).length > 0 || flags.current_medications
    || flags.known_allergies || flags.prev_gym_experience || flags.preferred_workout_time
    || flags.referral_source;

  return (
    <div data-testid={ids.section} className="solid-surface rounded-[14px] overflow-hidden">
      <button
        data-testid={ids.toggle}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5"
      >
        <span className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>Full profile &amp; KYC</span>
        {open ? <ChevronUp size={16} style={{ color: "var(--scarlet-text-secondary)" }} /> : <ChevronDown size={16} style={{ color: "var(--scarlet-text-secondary)" }} />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-5" style={{ borderTop: "1px solid var(--scarlet-hairline)", paddingTop: "16px" }}>
          <div className="flex items-center gap-3">
            <div
              data-testid={ids.photo}
              className="rounded-full overflow-hidden flex items-center justify-center shrink-0"
              style={{ width: 52, height: 52, background: "var(--scarlet-hairline)" }}
            >
              {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover" /> : <User size={20} style={{ color: "var(--scarlet-text-secondary)" }} />}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 flex-1">
              <Field testId={ids.memberSince} label="Member since" value={formatDate(member.created_at)} />
              <Field testId={ids.lastVisit} label="Last visit" value={formatDate(member.last_visit_at)} />
            </div>
          </div>

          <div>
            <SectionLabel>Personal details</SectionLabel>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              <Field testId={ids.dob} label="Date of birth" value={formatDate(profile.dob) ? `${formatDate(profile.dob)}${age != null ? ` (${age} yrs)` : ""}` : null} />
              <Field testId={ids.gender} label="Gender" value={GENDER_LABELS[profile.gender] || profile.gender} />
              <Field testId={ids.bloodGroup} label="Blood group" value={profile.blood_group} />
              <Field testId={ids.occupation} label="Occupation" value={profile.occupation} />
              <Field testId={ids.whatsapp} label="WhatsApp" value={profile.whatsapp} />
              <Field testId={ids.alternateMobile} label="Alternate mobile" value={profile.alternate_mobile} />
              <Field testId={ids.email} label="Email" value={profile.email} />
              <Field testId={ids.sourceAttribution} label="Source" value={titleCase(member.source_attribution)} />
            </div>
          </div>

          {emergency && (
            <div data-testid={ids.emergencyContact}>
              <SectionLabel>Emergency contact</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                <Field label="Name" value={emergency.name} />
                <Field label="Relationship" value={emergency.relationship} />
                <Field label="Mobile" value={emergency.mobile} />
                <Field label="Alt mobile" value={emergency.alt_mobile} />
              </div>
            </div>
          )}

          {hasKyc && (
            <div>
              <SectionLabel>KYC documents</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                <Field testId={ids.kycAadhaar} label="Aadhaar number" value={kyc.aadhaar_number} />
                <Field testId={ids.kycPan} label="PAN number" value={kyc.pan_number} />
                <Field testId={ids.kycOtherDoc} label={kyc.other_document_type || "Other document"} value={kyc.other_document_number} />
              </div>
            </div>
          )}

          {hasHealth && (
            <div data-testid={ids.medicalFlags}>
              <SectionLabel>Health &amp; fitness</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                <Field label="Fitness goal" value={titleCase(flags.fitness_goal)} />
                <Field label="Fitness level" value={titleCase(flags.fitness_level)} />
                <Field label="Previous gym experience" value={titleCase(flags.prev_gym_experience)} />
                <Field label="Preferred workout time" value={titleCase(flags.preferred_workout_time)} />
                <Field label="Referral source" value={titleCase(flags.referral_source)} />
                <Field label="Current medications" value={flags.current_medications} />
                <Field label="Known allergies" value={flags.known_allergies} />
              </div>
              {flags.medical_history && (
                <div className="mt-3">
                  <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Medical history</p>
                  <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text)" }}>{flags.medical_history}</p>
                </div>
              )}
              {(flags.medical_conditions || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {flags.medical_conditions.map((c) => (
                    <span key={c} className="status-pill overdue">{titleCase(c)}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <SectionLabel>Staff notes ({notes.length})</SectionLabel>
            {notes.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No notes on file yet.</p>
            ) : (
              <div data-testid={ids.notes} className="space-y-2">
                {notes.slice().reverse().map((n, i) => (
                  <div key={i} className="rounded-[10px] px-3 py-2" style={{ background: "var(--scarlet-hairline)" }}>
                    <p className="text-sm" style={{ color: "var(--scarlet-text)" }}>{n.text}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>{formatDate(n.at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
